import { NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import {
  DocumentChange,
  DocumentData,
  DocumentReference,
  Query,
  Unsubscribe,
  onSnapshot,
  QueryDocumentSnapshot,
} from '@firebase/firestore';

interface ICollectionDataOptions {
  idField?: string;
}

export function observeCollectionData<T extends DocumentData>(
  firestoreQuery: Query<T>,
  ngZone: NgZone,
  options?: ICollectionDataOptions
): Observable<Array<T & Record<string, unknown>>> {
  return new Observable(subscriber => {
    const unsubscribe = onSnapshot(
      firestoreQuery,
      snapshot => {
        const data = snapshot.docs.map(docSnapshot => mapDocWithId(docSnapshot, options?.idField));
        ngZone.run(() => subscriber.next(data));
      },
      error => ngZone.run(() => subscriber.error(error))
    );

    return () => unsubscribe();
  });
}

export function observeCollectionChanges<T extends DocumentData>(
  firestoreQuery: Query<T>,
  ngZone: NgZone
): Observable<Array<DocumentChange<T>>> {
  return new Observable(subscriber => {
    const unsubscribe: Unsubscribe = onSnapshot(
      firestoreQuery,
      snapshot => ngZone.run(() => subscriber.next(snapshot.docChanges())),
      error => ngZone.run(() => subscriber.error(error))
    );

    return () => unsubscribe();
  });
}

export function observeDocData<T extends DocumentData>(docRef: DocumentReference<T>, ngZone: NgZone): Observable<T | undefined> {
  return new Observable(subscriber => {
    const unsubscribe = onSnapshot(
      docRef,
      snapshot => {
        ngZone.run(() => subscriber.next(snapshot.exists() ? snapshot.data() : undefined));
      },
      error => ngZone.run(() => subscriber.error(error))
    );

    return () => unsubscribe();
  });
}

function mapDocWithId<T extends DocumentData>(docSnapshot: QueryDocumentSnapshot<T>, idField?: string): T & Record<string, unknown> {
  const data = docSnapshot.data();

  if (!idField) {
    return data as T & Record<string, unknown>;
  }

  return {
    ...data,
    [idField]: docSnapshot.id,
  };
}
